// Priority leads queue.
//
// At 10K clients, an unranked lead list buries the T65 45-days-out
// Medicare opportunity underneath 200 cold ACA inquiries. This view
// reads funnel_b_leads sorted by lead_score DESC and surfaces:
//
//   · Composite lead_score badge (green ≥600, amber 300-599, slate <300)
//   · source_tag badge (facebook_ad, referral_from_client, sep_trigger, etc.)
//   · Speed-to-contact countdown — seconds since arrival, red at >5 min,
//     amber at >2 min, green at <2 min. Insurance leads decay hard past
//     the 5-minute mark.
//   · Filters: source_tag chip row, min_score slider, hide-contacted toggle
//
// Read-only surface. Actions (call / SMS / assign) route to the
// existing lead detail modal via the /leads?id=xxx hash so we don't
// reinvent contact plumbing.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';
import { useIsDemoMode, useDemoLeads } from '../demo/DemoDataProvider';

// Fire the manual contact mark endpoint. Best-effort; failures are
// silent so the tel: link + UI still respond even if the network
// hiccups. Cheap idempotency — the server no-ops if first_contact_at
// is already set.
async function markContact(leadId) {
  try {
    const jwt = readPersistedJwtDirect();
    await fetch('/api/subscriber/mark-lead-contact', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify({ lead_id: leadId }),
    });
  } catch { /* silent */ }
}

// ── helpers ──────────────────────────────────────────────────────

function scoreTone(score) {
  if (score == null)     return 'bg-slate-100 text-slate-500';
  if (score >= 600)      return 'bg-emerald-100 text-emerald-900';
  if (score >= 300)      return 'bg-amber-100 text-amber-900';
  return 'bg-slate-100 text-slate-700';
}

function speedTone(seconds) {
  if (seconds == null)     return 'text-slate-400';
  if (seconds < 120)       return 'text-emerald-700';   // <2 min: green
  if (seconds < 300)       return 'text-amber-700';     // 2-5 min: amber
  return 'text-red-700';                                 // >5 min: red
}

function fmtDuration(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (seconds < 60)    return `${Math.floor(seconds)}s`;
  if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d`;
}

const SOURCE_TAG_LABELS = {
  facebook_ad:          { label: 'FB Ad',       tone: 'bg-blue-100 text-blue-900' },
  referral_from_client: { label: 'Referral',    tone: 'bg-fuchsia-100 text-fuchsia-900' },
  sep_trigger:          { label: 'SEP',         tone: 'bg-orange-100 text-orange-900' },
  aep_campaign:         { label: 'AEP',         tone: 'bg-purple-100 text-purple-900' },
  organic_web:          { label: 'Organic',     tone: 'bg-emerald-100 text-emerald-900' },
  purchased_list:       { label: 'Purchased',   tone: 'bg-slate-100 text-slate-700' },
  csv_import:           { label: 'CSV',         tone: 'bg-slate-100 text-slate-700' },
  unknown:              { label: 'Unknown',     tone: 'bg-slate-100 text-slate-500' },
};

const NICHE_LABELS = {
  medicare: 'Medicare',
  life:     'Life',
  aca:      'ACA',
  pc:       'P&C',
  unknown:  'Unknown',
};

// ── card ─────────────────────────────────────────────────────────

function LeadCard({ lead, nowMs, onOpen }) {
  const source = SOURCE_TAG_LABELS[lead.source_tag] || SOURCE_TAG_LABELS.unknown;
  const arrivedAt = lead.created_at ? new Date(lead.created_at).getTime() : null;
  const secondsSinceArrival = (arrivedAt && !lead.first_contact_at)
    ? Math.max(0, Math.floor((nowMs - arrivedAt) / 1000))
    : null;
  const speedToContact = lead.speed_to_contact_seconds;

  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || '(no name)';

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider ${scoreTone(lead.lead_score)}`}>
              {lead.lead_score ?? '—'}
            </span>
            <span className={`rounded px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider ${source.tone}`}>
              {source.label}
            </span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider text-slate-700">
              {NICHE_LABELS[lead.niche] || lead.niche || 'Unknown'}
            </span>
            {lead.state_code && (
              <span className="text-[0.6rem] font-semibold text-slate-500">{lead.state_code}</span>
            )}
          </div>
          <div className="mt-1 font-semibold text-slate-800 truncate">{name}</div>
          <div className="text-xs text-slate-500 truncate">
            {lead.phone_e164 || '—'} {lead.email ? ` · ${lead.email}` : ''}
          </div>
          <div className="mt-1 flex items-center gap-3 text-[0.65rem]">
            {secondsSinceArrival != null ? (
              <span className={`font-semibold ${speedTone(secondsSinceArrival)}`}>
                ⏱ {fmtDuration(secondsSinceArrival)} since arrival · uncontacted
              </span>
            ) : (
              <span className="text-slate-500">
                {speedToContact != null
                  ? `First contact in ${fmtDuration(speedToContact)}`
                  : `Contacted`}
              </span>
            )}
            <span className="text-slate-400">stage: {lead.stage}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {lead.phone_e164 && (
            <a
              href={`tel:${lead.phone_e164}`}
              onClick={() => markContact(lead.id)}
              className="rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-900 hover:bg-blue-200"
            >📞 Call</a>
          )}
          <button
            type="button"
            onClick={() => onOpen(lead)}
            className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
          >Open</button>
        </div>
      </div>
      {lead.lead_score_breakdown && (
        <details className="mt-2 text-[0.6rem] text-slate-500">
          <summary className="cursor-pointer hover:text-slate-700">Score breakdown</summary>
          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono">
            {Object.entries(lead.lead_score_breakdown).map(([k, v]) => (
              <div key={k}>
                <span className="text-slate-400">{k}:</span> {v > 0 ? '+' : ''}{v}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ── main view ────────────────────────────────────────────────────

export default function PriorityLeadsView({ currentUser, showToast = () => {}, onGotoTab = () => {} }) {
  const [leads, setLeads]           = React.useState([]);
  const [loading, setLoading]       = React.useState(true);
  const [minScore, setMinScore]     = React.useState(200);
  const [sourceFilter, setSourceFilter] = React.useState('all');
  const [hideContacted, setHideContacted] = React.useState(false);
  const [nowMs, setNowMs]           = React.useState(Date.now());

  // Tick the clock every 5 seconds so the "seconds since arrival"
  // counter animates without paying for higher fidelity.
  React.useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  const isDemo    = useIsDemoMode();
  const demoLeads = useDemoLeads();

  const refresh = React.useCallback(async () => {
    setLoading(true);
    // Demo mode: skip supabase, serve the pre-scored SCORED_LEADS
    // fixture. Same shape as the DB row so the render code is
    // identical.
    if (isDemo && Array.isArray(demoLeads)) {
      const shaped = demoLeads.map(l => ({
        id:                       l.id,
        first_name:               l.firstName || (l.name || '').split(' ')[0],
        last_name:                l.lastName  || (l.name || '').split(' ').slice(1).join(' '),
        phone_e164:               l.phone_e164 || l.phone,
        email:                    l.email,
        state_code:               l.state_code || l.state,
        niche:                    l.leadType === 'medicare' ? 'medicare' : (l.vertical === 'life' ? 'life' : (l.vertical === 'health' ? 'aca' : 'unknown')),
        source_tag:               l.source_tag,
        lead_score:               l.lead_score,
        lead_score_breakdown:     l.lead_score_breakdown,
        stage:                    l.stage === 'appointment_set' ? 'booked' : (l.stage === 'sold' ? 'sold' : 'new'),
        first_contact_at:         l.first_contact_at,
        speed_to_contact_seconds: l.speed_to_contact_seconds,
        created_at:               l.created_at || l.createdAt,
      })).filter(l => ['new', 'working'].includes(l.stage))
         .sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0));
      setLeads(shaped);
      setLoading(false);
      return;
    }
    try {
      let q = supabase
        .from('funnel_b_leads')
        .select('id, first_name, last_name, phone_e164, email, state_code, niche, source_tag, lead_score, lead_score_breakdown, stage, first_contact_at, speed_to_contact_seconds, created_at')
        .in('stage', ['new', 'working'])
        .order('lead_score', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(200);
      const { data, error } = await q;
      if (error) throw error;
      setLeads(data || []);
    } catch (e) {
      showToast(`Load failed: ${e.message}`);
    }
    setLoading(false);
  }, [showToast, isDemo, demoLeads]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const filtered = React.useMemo(() => {
    return leads.filter(l => {
      if (l.lead_score != null && l.lead_score < minScore) return false;
      if (sourceFilter !== 'all' && l.source_tag !== sourceFilter) return false;
      if (hideContacted && l.first_contact_at) return false;
      return true;
    });
  }, [leads, minScore, sourceFilter, hideContacted]);

  const sourceCounts = React.useMemo(() => {
    const counts = {};
    for (const l of leads) counts[l.source_tag || 'unknown'] = (counts[l.source_tag || 'unknown'] || 0) + 1;
    return counts;
  }, [leads]);

  const uncontactedCount = leads.filter(l => !l.first_contact_at).length;
  const overSlaCount = leads.filter(l => {
    if (l.first_contact_at) return false;
    const secs = (nowMs - new Date(l.created_at).getTime()) / 1000;
    return secs > 300;   // >5 min SLA
  }).length;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold text-slate-900">Leads · Priority Queue</h1>
          <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-fuchsia-900">
            Scored + speed-to-contact
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Sorted by composite lead_score DESC. Every card shows source, niche, state, live
          time-since-arrival, and expandable score breakdown. Red timer = past the 5-minute
          SLA where insurance leads decay hard.
        </p>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Uncontacted</div>
          <div className="text-lg font-bold">{uncontactedCount}</div>
        </div>
        <div className={`rounded-lg border p-3 ${overSlaCount > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
          <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Past 5-min SLA</div>
          <div className={`text-lg font-bold ${overSlaCount > 0 ? 'text-red-800' : ''}`}>{overSlaCount}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">In queue</div>
          <div className="text-lg font-bold">{leads.length}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 space-y-2">
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setSourceFilter('all')}
            className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${sourceFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >All ({leads.length})</button>
          {Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]).map(([tag, count]) => {
            const s = SOURCE_TAG_LABELS[tag] || SOURCE_TAG_LABELS.unknown;
            return (
              <button
                key={tag}
                type="button"
                onClick={() => setSourceFilter(tag)}
                className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${sourceFilter === tag ? 'ring-2 ring-blue-400 ' : ''}${s.tone} hover:brightness-95`}
              >{s.label} ({count})</button>
            );
          })}
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <label className="flex items-center gap-2">
            <span className="whitespace-nowrap">Min score:</span>
            <input
              type="range"
              min="0" max="1000" step="50"
              value={minScore}
              onChange={e => setMinScore(Number(e.target.value))}
              className="w-40"
            />
            <span className="font-mono font-semibold w-10">{minScore}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={hideContacted}
              onChange={e => setHideContacted(e.target.checked)}
            />
            <span>Hide contacted</span>
          </label>
          <button
            type="button"
            onClick={refresh}
            className="ml-auto rounded bg-slate-100 px-2 py-1 font-semibold text-slate-700 hover:bg-slate-200"
          >Refresh</button>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
          No leads match the current filters. Lower the min score or clear the source filter.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(l => (
          <LeadCard
            key={l.id}
            lead={l}
            nowMs={nowMs}
            onOpen={(lead) => {
              // Route to the existing leads detail flow if you have one wired.
              onGotoTab && onGotoTab('leads', { openLeadId: lead.id });
            }}
          />
        ))}
      </div>
    </div>
  );
}
